/*!
 * SkyPlus Search Component
 * Location: app/src/app/components/skyplus/search/skyplus-search.component.ts
 * 
 * Features:
 * - Simple search (by file name)
 * - Advanced search (by document type/aspect properties)
 * - Reuses document types from indexing configuration
 * - SkyPlus themed UI
 */

import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { WfDocumentTypeRegistryService } from '../indexing/document-type-registry.service';
import { WfSearchApiService, WfSearchRequest } from './wf-search-api.service';
import { Router } from '@angular/router';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { 
  DocumentTypeConfig, 
  DocumentField,
  getEnabledDocumentTypes 
} from '../indexing/document-types.config';

// Search result interface
export interface SearchResult {
  id: string;
  name: string;
  title?: string;
  description?: string;
  path: string;
  mimeType?: string;
  size?: number;
  sizeFormatted?: string;
  createdAt: Date;
  createdBy: string;
  modifiedAt: Date;
  modifiedBy: string;
  icon: string;
  iconColor: string;
  isFolder: boolean;
  properties?: any;
  highlights?: string[];
}

// Search criteria
export interface SearchCriteria {
  searchTerm?: string;
  documentType?: DocumentTypeConfig;
  fieldValues: { [propertyName: string]: any };
  dateFrom?: string;
  dateTo?: string;
  location?: string;
  includeSubfolders?: boolean;
}

@Component({
  selector: 'sp-search',
  templateUrl: './skyplus-search.component.html',
  styleUrls: ['./skyplus-search.component.scss']
})
export class SkyPlusSearchComponent implements OnInit, OnDestroy {
  
  @Output() resultSelected = new EventEmitter<SearchResult>();
  @Output() searchCompleted = new EventEmitter<SearchResult[]>();
  
  // Search modes
  searchMode: 'simple' | 'advanced' = 'simple';
  
  // Simple search
  simpleSearchTerm = '';
  
  // Advanced search
  documentTypes: DocumentTypeConfig[] = [];
  selectedDocumentType: DocumentTypeConfig | null = null;
  fieldValues: { [propertyName: string]: any } = {};
  dateFrom = '';
  dateTo = '';
  searchLocation = '';
  includeSubfolders = true;
  
  // Results
  searchResults: SearchResult[] = [];
  isSearching = false;
  hasSearched = false;
  totalResults = 0;
  
  // Pagination
  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];
  
  // View mode
  viewMode: 'list' | 'grid' = 'list';
  
  // Selected result for properties panel
  selectedResult: SearchResult | null = null;
  showPropertiesPanel = false;
  
  // Search suggestions
  searchSubject = new Subject<string>();
  suggestions: string[] = [];
  showSuggestions = false;
  
  private destroy$ = new Subject<void>();
  
  private ecmHost = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: AuthenticationService,
    private appConfig: AppConfigService,
    private registry: WfDocumentTypeRegistryService,
    private searchApi: WfSearchApiService
  ) {
    this.ecmHost = (this.appConfig.get<string>('ecmHost', '') || '').replace(/\/+$/, '');
  }

  /** Build a full Alfresco URL via ecmHost (routes through nginx + Bearer auth). */
  private getApiUrl(path: string): string { return `${this.ecmHost}${path}`; }

  /** Bearer (Keycloak) auth header — replaces the legacy alf_ticket approach. */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
  
  ngOnInit(): void {
    // Load document types — static immediately, then refresh from the registry.
    this.documentTypes = getEnabledDocumentTypes();
    this.registry.load().then(() => {
      this.documentTypes = getEnabledDocumentTypes();
    });
    
    // Setup search debounce for suggestions
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      if (term.length >= 2) {
        this.loadSuggestions(term);
      } else {
        this.suggestions = [];
        this.showSuggestions = false;
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  // ============================================================================
  // SEARCH MODE TOGGLE
  // ============================================================================
  
  setSearchMode(mode: 'simple' | 'advanced'): void {
    this.searchMode = mode;
    this.clearSearch();
  }
  
  // ============================================================================
  // SIMPLE SEARCH
  // ============================================================================
  
  onSimpleSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.simpleSearchTerm = value;
    this.searchSubject.next(value);
  }
  
  async executeSimpleSearch(): Promise<void> {
    if (!this.simpleSearchTerm.trim()) {
      return;
    }
    
    this.showSuggestions = false;
    this.isSearching = true;
    this.hasSearched = true;
    this.currentPage = 1;
    
    try {
      // Round 6 — unified backend search (registry-aware, engine fallback).
      await this.runUnifiedSearch();
    } catch (backendError) {
      // Older backend / endpoint unreachable — keep the legacy direct
      // Alfresco path so search never regresses below today's behaviour.
      console.warn('Unified search unavailable — falling back to direct AFTS:', backendError);
      try {
        const query = this.buildSimpleSearchQuery(this.simpleSearchTerm);
        await this.executeSearch(query);
      } catch (error) {
        console.error('Simple search error:', error);
        this.searchResults = [];
      }
    } finally {
      this.isSearching = false;
    }
  }
  
  private buildSimpleSearchQuery(term: string): string {
    // Escape special characters
    const escapedTerm = term.replace(/[+\-&|!(){}[\]^"~*?:\\]/g, '\\$&');
    
    // Search in name, title, description, and content
    return `(name:"*${escapedTerm}*" OR cm:title:"*${escapedTerm}*" OR cm:description:"*${escapedTerm}*" OR TEXT:"${escapedTerm}")`;
  }
  
  // ============================================================================
  // ADVANCED SEARCH
  // ============================================================================
  
  selectDocumentType(docType: DocumentTypeConfig | null): void {
    this.selectedDocumentType = docType;
    // Reset field values when document type changes
    this.fieldValues = {};
    
    // Initialize field values with empty strings
    if (docType) {
      docType.fields.forEach(field => {
        this.fieldValues[field.propertyName] = '';
      });
    }
  }
  
  async executeAdvancedSearch(): Promise<void> {
    this.isSearching = true;
    this.hasSearched = true;
    this.currentPage = 1;
    
    try {
      // Round 6 — unified backend search. Field types + aspects resolve
      // server-side from the document-type registry; field values and the
      // selected type's key travel as intent.
      await this.runUnifiedSearch();
    } catch (backendError) {
      console.warn('Unified search unavailable — falling back to direct AFTS:', backendError);
      try {
        const query = this.buildAdvancedSearchQuery();
        await this.executeSearch(query);
      } catch (error) {
        console.error('Advanced search error:', error);
        this.searchResults = [];
      }
    } finally {
      this.isSearching = false;
    }
  }

  // ============================================================================
  // ROUND 6 — UNIFIED BACKEND SEARCH
  // ============================================================================

  /**
   * Builds the intent payload for the current mode/page and executes it via
   * POST /api/wf/search. Results arrive as raw Alfresco node entries, so the
   * existing {@link mapNodeToSearchResult} mapping is reused verbatim.
   * Throws on transport failure so callers can fall back to the legacy path.
   */
  private async runUnifiedSearch(): Promise<void> {
    const request = this.buildUnifiedRequest();
    const response = await firstValueFrom(this.searchApi.search(request));

    this.totalResults = response.totalItems || 0;
    let results = (response.entries || []).map((node: any) => this.mapNodeToSearchResult(node));

    // The optional "Location" path text is applied client-side — the
    // backend scopes by folder id, not free-typed paths.
    const loc = (this.searchLocation || '').trim().toLowerCase();
    if (this.searchMode === 'advanced' && loc) {
      results = results.filter(r => (r.path || '').toLowerCase().includes(loc));
    }

    this.searchResults = results;
    console.log(`[search] unified engine='${response.engine}' total=${this.totalResults}`);
    this.searchCompleted.emit(this.searchResults);
  }

  /** Intent payload for the current UI state (mode, fields, page). */
  private buildUnifiedRequest(): WfSearchRequest {
    const paging = {
      maxItems: this.pageSize,
      skipCount: (this.currentPage - 1) * this.pageSize
    };

    if (this.searchMode === 'simple') {
      return {
        term: this.simpleSearchTerm.trim(),
        includeFolders: true,
        ...paging
      };
    }

    // Advanced — send the document type key (registry-resolved server-side)
    // plus aspect/fieldTypes for legacy static types, and only non-empty
    // field values.
    const fields: Record<string, string> = {};
    const fieldTypes: Record<string, string> = {};
    (this.selectedDocumentType?.fields || [])
      .filter(f => f.search !== false)
      .forEach(f => {
        const v = this.fieldValues[f.propertyName];
        if (v !== undefined && v !== null && `${v}`.trim() !== '') {
          fields[f.propertyName] = `${v}`.trim();
          fieldTypes[f.propertyName] = f.type;
        }
      });

    return {
      documentTypeKey: this.selectedDocumentType?.id,
      aspectName: this.selectedDocumentType?.aspectName,
      fields,
      fieldTypes,
      dateFrom: this.dateFrom || undefined,
      dateTo: this.dateTo || undefined,
      ...paging
    };
  }
  
  private buildAdvancedSearchQuery(): string {
    const conditions: string[] = [];
    
    // Add document type/aspect filter
    if (this.selectedDocumentType) {
      conditions.push(`ASPECT:"${this.selectedDocumentType.aspectName}"`);
      
      // Add field conditions
      this.selectedDocumentType.fields
        .filter(field => field.search !== false)
        .forEach(field => {
        const value = this.fieldValues[field.propertyName];
        if (value !== undefined && value !== null && value !== '') {
          if (field.type === 'date') {
            // Date field - exact match or range
            conditions.push(`@${field.propertyName}:"${value}"`);
          } else if (field.type === 'number') {
            // Number field - exact match
            conditions.push(`@${field.propertyName}:${value}`);
          } else if (field.type === 'select') {
            // Select field - exact match
            conditions.push(`@${field.propertyName}:"${value}"`);
          } else {
            // Text field - wildcard search
            conditions.push(`@${field.propertyName}:"*${value}*"`);
          }
        }
      });
    }
    
    // Add date range filter
    if (this.dateFrom || this.dateTo) {
      const from = this.dateFrom ? `${this.dateFrom}T00:00:00` : 'MIN';
      const to = this.dateTo ? `${this.dateTo}T23:59:59` : 'MAX';
      conditions.push(`cm:created:[${from} TO ${to}]`);
    }
    
    // Add location filter
    if (this.searchLocation) {
      conditions.push(`PATH:"/app:company_home${this.searchLocation.replace(/\//g, '/cm:')}/*"`);
    }
    
    // If no conditions, search all documents
    if (conditions.length === 0) {
      return 'TYPE:"cm:content"';
    }
    
    return conditions.join(' AND ');
  }
  
  // ============================================================================
  // EXECUTE SEARCH
  // ============================================================================
  
  private async executeSearch(query: string): Promise<void> {
    const searchUrl = this.getApiUrl('/alfresco/api/-default-/public/search/versions/1/search');
    
    const searchRequest = {
      query: {
        query: query,
        language: 'afts'
      },
      include: ['properties', 'path', 'allowableOperations'],
      paging: {
        maxItems: this.pageSize,
        skipCount: (this.currentPage - 1) * this.pageSize
      },
      sort: [
        { type: 'FIELD', field: 'cm:modified', ascending: false }
      ]
    };
    
    console.log('Search query:', query);
    console.log('Search request:', searchRequest);
    
    try {
      const response: any = await this.http.post(searchUrl, searchRequest, { headers: this.getAuthHeaders() }).toPromise();
      
      this.totalResults = response.list?.pagination?.totalItems || 0;
      
      this.searchResults = (response.list?.entries || []).map((entry: any) => {
        const node = entry.entry;
        return this.mapNodeToSearchResult(node);
      });
      
      this.searchCompleted.emit(this.searchResults);
      
    } catch (error) {
      console.error('Search API error:', error);
      throw error;
    }
  }
  
  private mapNodeToSearchResult(node: any): SearchResult {
    const isFolder = node.isFolder || node.nodeType === 'cm:folder';
    
    return {
      id: node.id,
      name: node.name,
      title: node.properties?.['cm:title'],
      description: node.properties?.['cm:description'],
      path: node.path?.name || '',
      mimeType: node.content?.mimeType,
      size: node.content?.sizeInBytes,
      sizeFormatted: this.formatFileSize(node.content?.sizeInBytes || 0),
      createdAt: new Date(node.createdAt),
      createdBy: node.createdByUser?.displayName || 'Unknown',
      modifiedAt: new Date(node.modifiedAt),
      modifiedBy: node.modifiedByUser?.displayName || 'Unknown',
      icon: this.getFileIcon(node.name, isFolder, node.content?.mimeType),
      iconColor: this.getIconColor(node.name, isFolder, node.content?.mimeType),
      isFolder: isFolder,
      properties: node.properties
    };
  }
  
  // ============================================================================
  // SUGGESTIONS
  // ============================================================================
  
  private async loadSuggestions(term: string): Promise<void> {
    // Round 6 — suggestions ride the unified endpoint too (name matches work
    // even without Solr). Legacy direct AFTS remains the fallback.
    try {
      const response = await firstValueFrom(this.searchApi.search({
        term, includeFolders: false, maxItems: 5, skipCount: 0
      }));
      this.suggestions = (response.entries || [])
        .map((node: any) => node.name)
        .filter((name: string, index: number, self: string[]) => !!name && self.indexOf(name) === index);
      this.showSuggestions = this.suggestions.length > 0;
      return;
    } catch (backendError) {
      console.warn('Unified suggestions unavailable — legacy fallback:', backendError);
    }

    try {
      const searchUrl = this.getApiUrl('/alfresco/api/-default-/public/search/versions/1/search');
      
      const searchRequest = {
        query: {
          query: `name:"*${term}*"`,
          language: 'afts'
        },
        paging: {
          maxItems: 5,
          skipCount: 0
        },
        fields: ['name']
      };
      
      const response: any = await this.http.post(searchUrl, searchRequest, { headers: this.getAuthHeaders() }).toPromise();
      
      this.suggestions = (response.list?.entries || [])
        .map((entry: any) => entry.entry.name)
        .filter((name: string, index: number, self: string[]) => self.indexOf(name) === index);
      
      this.showSuggestions = this.suggestions.length > 0;
      
    } catch (error) {
      console.error('Error loading suggestions:', error);
      this.suggestions = [];
    }
  }
  
  selectSuggestion(suggestion: string): void {
    this.simpleSearchTerm = suggestion;
    this.showSuggestions = false;
    this.executeSimpleSearch();
  }
  
  hideSuggestions(): void {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);
  }
  
  // ============================================================================
  // PAGINATION
  // ============================================================================
  
  get totalPages(): number {
    return Math.ceil(this.totalResults / this.pageSize);
  }
  
  get startItem(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }
  
  get endItem(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalResults);
  }
  
  async onPageChange(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages) return;
    
    this.currentPage = page;
    
    try {
      // Round 6 — paging reruns the unified search for the current mode.
      await this.runUnifiedSearch();
    } catch (backendError) {
      console.warn('Unified search unavailable on page change — legacy fallback:', backendError);
      if (this.searchMode === 'simple') {
        const query = this.buildSimpleSearchQuery(this.simpleSearchTerm);
        await this.executeSearch(query);
      } else {
        const query = this.buildAdvancedSearchQuery();
        await this.executeSearch(query);
      }
    }
  }
  
  async onPageSizeChange(size: number): Promise<void> {
    this.pageSize = size;
    this.currentPage = 1;
    
    if (this.hasSearched) {
      if (this.searchMode === 'simple') {
        await this.executeSimpleSearch();
      } else {
        await this.executeAdvancedSearch();
      }
    }
  }
  
  // ============================================================================
  // RESULT ACTIONS
  // ============================================================================
  
  selectResult(result: SearchResult, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.selectedResult = result;
    this.showPropertiesPanel = true;
    this.resultSelected.emit(result);
  }
  
  openResult(result: SearchResult): void {
    if (result.isFolder) {
      // Navigate to folder in document library
      this.router.navigate(['/documents'], { queryParams: { folderId: result.id } });
    } else {
      // Open document viewer
      this.openDocumentViewer(result);
    }
  }
  
  openDocumentViewer(result: SearchResult): void {
    const ticket = localStorage.getItem('ticket-ECM');
    let viewerUrl = `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${result.id}/content`;
    if (ticket) {
      viewerUrl += `?alf_ticket=${ticket}&attachment=false`;
    }
    window.open(viewerUrl, '_blank');
  }
  
  downloadResult(result: SearchResult, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    
    const ticket = localStorage.getItem('ticket-ECM');
    let downloadUrl = `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${result.id}/content`;
    if (ticket) {
      downloadUrl += `?alf_ticket=${ticket}&attachment=true`;
    }
    window.open(downloadUrl, '_blank');
  }
  
  navigateToLocation(_result: SearchResult, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    
    // Extract parent folder ID from path and navigate
    // For now, navigate to document library
    this.router.navigate(['/documents']);
  }
  
  closePropertiesPanel(): void {
    this.showPropertiesPanel = false;
    this.selectedResult = null;
  }
  
  // Get custom properties for display (filter out cm:, sys:, app: prefixes)
  getCustomProperties(properties: any): { label: string; value: any }[] {
    if (!properties) return [];
    
    const customProps: { label: string; value: any }[] = [];
    
    Object.keys(properties).forEach(key => {
      if (!key.startsWith('cm:') && !key.startsWith('sys:') && !key.startsWith('app:')) {
        const value = properties[key];
        if (value !== null && value !== undefined && value !== '') {
          const label = key.includes(':') ? key.split(':')[1] : key;
          customProps.push({
            label: label.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim(),
            value: value
          });
        }
      }
    });
    
    return customProps;
  }
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  clearSearch(): void {
    this.simpleSearchTerm = '';
    this.selectedDocumentType = null;
    this.fieldValues = {};
    this.dateFrom = '';
    this.dateTo = '';
    this.searchLocation = '';
    this.searchResults = [];
    this.hasSearched = false;
    this.totalResults = 0;
    this.currentPage = 1;
    this.suggestions = [];
    this.showSuggestions = false;
    this.closePropertiesPanel();
  }
  
  setViewMode(mode: 'list' | 'grid'): void {
    this.viewMode = mode;
  }
  
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  formatDate(date: Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  formatPath(path: string): string {
    if (!path) return '/';
    return path
      .replace('/Company Home/User Homes/', '~/')
      .replace('/Company Home/', '/')
      .replace(/^\/+/, '') || '/';
  }
  
  private getFileIcon(name: string, isFolder: boolean, _mimeType?: string): string {
    if (isFolder) return 'folder';
    
    const ext = name.split('.').pop()?.toLowerCase() || '';
    
    const iconMap: { [key: string]: string } = {
      'pdf': 'picture_as_pdf',
      'doc': 'description',
      'docx': 'description',
      'xls': 'table_chart',
      'xlsx': 'table_chart',
      'ppt': 'slideshow',
      'pptx': 'slideshow',
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image',
      'gif': 'image',
      'txt': 'article',
      'csv': 'table_chart',
      'zip': 'folder_zip',
      'rar': 'folder_zip',
      'mp4': 'movie',
      'mp3': 'audiotrack',
      'html': 'code',
      'xml': 'code',
      'json': 'code'
    };
    
    return iconMap[ext] || 'insert_drive_file';
  }
  
  private getIconColor(name: string, isFolder: boolean, _mimeType?: string): string {
    if (isFolder) return '#FFC107';
    
    const ext = name.split('.').pop()?.toLowerCase() || '';
    
    const colorMap: { [key: string]: string } = {
      'pdf': '#D32F2F',
      'doc': '#1976D2',
      'docx': '#1976D2',
      'xls': '#388E3C',
      'xlsx': '#388E3C',
      'ppt': '#F57C00',
      'pptx': '#F57C00',
      'jpg': '#7B1FA2',
      'jpeg': '#7B1FA2',
      'png': '#7B1FA2',
      'gif': '#7B1FA2',
      'txt': '#616161',
      'csv': '#388E3C',
      'zip': '#795548',
      'rar': '#795548'
    };
    
    return colorMap[ext] || '#757575';
  }
  
  // Track by for ngFor
  trackByResultId(_index: number, result: SearchResult): string {
    return result.id;
  }
  
  trackByFieldName(_index: number, field: DocumentField): string {
    return field.propertyName;
  }

  /** Strike A — only fields flagged for search (undefined = searchable, legacy types). */
  searchableFields(): DocumentField[] {
    return (this.selectedDocumentType?.fields || []).filter(f => f.search !== false);
  }
}